import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { baseurImage } from "../../Baseurl";
import axiosInstance from "../../api/axiosInstance";

export default function KioskCheckin() {
  const location = useLocation();
  const navigate = useNavigate();
  const patient = location.state?.patient;
  const businessSettings = useSelector((state) => state.business.businessSettings);

  const [appointments, setAppointments] = useState(null); // null = loading
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [actionLoading, setActionLoading] = useState(null); // appointmentId or "book"
  const [done, setDone] = useState(false); // thank-you screen

  useEffect(() => {
    if (!patient) return;
    axiosInstance
      .get(`getPatientTodayAppointments/${patient.id}`)
      .then((res) => setAppointments(res.data?.data || []))
      .catch(() => setAppointments([]));
  }, [patient?.id]);

  useEffect(() => {
    if (appointments && appointments.length === 0) {
      axiosInstance
        .get("getActiveDoctors")
        .then((res) => {
          const list = Array.isArray(res.data?.data) ? res.data.data : [];
          setDoctors(list);
          if (list.length > 0) setSelectedDoctor(String(list[0].id));
        })
        .catch(() => {});
    }
  }, [appointments]);

  if (!patient) {
    navigate("/Admin/Kiosk");
    return null;
  }

  const getLogoSrc = () => {
    const raw = businessSettings?.image || businessSettings?.logo;
    if (!raw) return null;
    if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
    return `${baseurImage}${raw}`;
  };

  const handleCheckin = async (appointmentId) => {
    setActionLoading(appointmentId);
    try {
      await axiosInstance.post(`logArrival/${appointmentId}`);
      setDone(true);
    } catch {
      alert("Something went wrong. Please try again.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleWalkIn = async () => {
    setActionLoading("book");
    try {
      await axiosInstance.post("kioskWalkIn", {
        patientId: patient.id,
        doctorId: selectedDoctor || null,
      });
      setDone(true);
    } catch {
      alert("Something went wrong. Please try again.");
    } finally {
      setActionLoading(null);
    }
  };

  const logoSrc = getLogoSrc();
  const fullName = [patient.firstName, patient.middleName, patient.lastName].filter(Boolean).join(" ");

  const formatTime = (t) => {
    if (!t) return "";
    try {
      return new Date(`1970-01-01T${t}`).toLocaleTimeString("en-US", {
        hour: "2-digit", minute: "2-digit", hour12: true,
      });
    } catch { return t; }
  };

  // ── Thank you screen ──────────────────────────────────────────────────────
  if (done) {
    return (
      <div style={styles.root}>
        {logoSrc && <img src={logoSrc} alt="Logo" style={{ ...styles.logo, marginBottom: 40 }} />}
        <div style={styles.thankCard}>
          <div style={styles.checkCircle}>
            <i className="ti ti-check" style={{ fontSize: 48, color: "#fff" }} />
          </div>
          <p style={styles.thankTitle}>Thank you, {patient.firstName}!</p>
          <p style={styles.thankSub}>
            Your request has been received.<br />
            Please take a seat and wait to be called.
          </p>
          <button
            style={styles.homeBtn}
            onClick={() => navigate("/Admin/Kiosk")}
          >
            <i className="ti ti-home me-2" />
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  // ── Main screen ───────────────────────────────────────────────────────────
  return (
    <div style={styles.root}>
      {/* Logo */}
      {logoSrc && <img src={logoSrc} alt="Logo" style={styles.logo} />}

      {/* Patient info — compact */}
      <div style={styles.patientBar}>
        <div style={styles.patientAvatar}>
          <i className="ti ti-user" style={{ fontSize: 22, color: "#4f8ef7" }} />
        </div>
        <div style={styles.patientMeta}>
          <span style={styles.patientName}>{fullName}</span>
          <span style={styles.patientDetail}>
            {patient.civilIdNumber}
            {patient.age != null ? ` · ${patient.age} yrs` : ""}
            {patient.gender ? ` · ${patient.gender}` : ""}
            {patient.mobileNumber ? ` · ${patient.mobileNumber}` : ""}
          </span>
        </div>
      </div>

      {/* Content area */}
      <div style={styles.content}>

        {/* Loading */}
        {appointments === null && (
          <div style={styles.stateBox}>
            <span className="spinner-border text-primary" style={{ width: 40, height: 40 }} />
            <p style={styles.stateText}>Checking appointments…</p>
          </div>
        )}

        {/* Has appointments today */}
        {appointments !== null && appointments.length > 0 && (
          <>
            <p style={styles.sectionTitle}>
              <i className="ti ti-calendar-check me-2" style={{ color: "#4f8ef7" }} />
              Your appointments today
            </p>
            <div style={styles.apptList}>
              {appointments.map((appt) => (
                <div key={appt.id} style={styles.apptCard}>
                  <div style={styles.apptLeft}>
                    <div style={styles.apptTime}>{formatTime(appt.startTime)}</div>
                    <div style={styles.apptDoctor}>
                      {appt.doctorDisplayName || "Walk-in"}
                    </div>
                    {appt.doctorSpecialty && (
                      <div style={styles.apptSpecialty}>{appt.doctorSpecialty}</div>
                    )}
                    <span style={{ ...styles.apptBadge, ...getStatusStyle(appt.status) }}>
                      {appt.status}
                    </span>
                  </div>
                  <button
                    style={{
                      ...styles.checkinBtn,
                      ...(actionLoading === appt.id ? styles.checkinBtnLoading : {}),
                    }}
                    onClick={() => handleCheckin(appt.id)}
                    disabled={actionLoading !== null}
                  >
                    {actionLoading === appt.id ? (
                      <span className="spinner-border spinner-border-sm" />
                    ) : (
                      <>
                        <i className="ti ti-login me-2" style={{ fontSize: 18 }} />
                        Check In
                      </>
                    )}
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {/* No appointments — walk-in */}
        {appointments !== null && appointments.length === 0 && (
          <>
            <div style={styles.noApptBox}>
              <i className="ti ti-calendar-x" style={{ fontSize: 36, color: "#b0bac8", marginBottom: 8 }} />
              <p style={styles.noApptTitle}>No appointments scheduled for today</p>
              <p style={styles.noApptSub}>You can walk in by selecting a doctor below</p>
            </div>

            <p style={styles.sectionTitle}>
              <i className="ti ti-stethoscope me-2" style={{ color: "#4f8ef7" }} />
              Select a doctor
            </p>

            {doctors.length === 0 ? (
              <p style={styles.stateText}>Loading doctors…</p>
            ) : (
              <div style={styles.doctorGrid}>
                {doctors.map((d) => {
                  const dName = `${d.prefix || ""} ${d.fullName || ""} ${d.shortName || ""}`.trim();
                  const isSelected = String(d.id) === String(selectedDoctor);
                  return (
                    <button
                      key={d.id}
                      style={{
                        ...styles.doctorCard,
                        ...(isSelected ? styles.doctorCardSelected : {}),
                      }}
                      onClick={() => setSelectedDoctor(String(d.id))}
                    >
                      <div style={styles.doctorAvatar}>
                        {d.personalPhoto ? (
                          <img
                            src={`${baseurImage}${d.personalPhoto}`}
                            alt={dName}
                            style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
                          />
                        ) : (
                          <i className="ti ti-user-circle" style={{ fontSize: 30, color: isSelected ? "#4f8ef7" : "#a0aab8" }} />
                        )}
                      </div>
                      <span style={styles.doctorName}>{dName}</span>
                      {d.specialty && <span style={styles.doctorSpec}>{d.specialty}</span>}
                      {isSelected && (
                        <div style={styles.selectedTick}>
                          <i className="ti ti-check" style={{ fontSize: 14, color: "#fff" }} />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            <button
              style={{
                ...styles.bookBtn,
                ...(selectedDoctor && actionLoading === null ? styles.bookBtnActive : {}),
              }}
              onClick={handleWalkIn}
              disabled={!selectedDoctor || actionLoading !== null}
            >
              {actionLoading === "book" ? (
                <span className="spinner-border spinner-border-sm me-2" />
              ) : (
                <i className="ti ti-calendar-plus me-2" style={{ fontSize: 20 }} />
              )}
              Book Walk-in
            </button>
          </>
        )}
      </div>

      {/* Back */}
      <button style={styles.backBtn} onClick={() => navigate("/Admin/Kiosk")}>
        <i className="ti ti-arrow-left me-2" />
        Back
      </button>
    </div>
  );
}

function getStatusStyle(status) {
  const map = {
    Booked: { background: "#e8f4ff", color: "#2563eb" },
    Waiting: { background: "#fff7e6", color: "#d97706" },
    Emergency: { background: "#ffeaea", color: "#dc2626" },
    Confirmed: { background: "#e8faf0", color: "#16a34a" },
  };
  return map[status] || { background: "#f0f0f0", color: "#555" };
}

const styles = {
  root: {
    minHeight: "100vh",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    background: "linear-gradient(160deg, #f0f4ff 0%, #ffffff 60%, #eaf6f6 100%)",
    padding: "28px 20px 24px",
    boxSizing: "border-box",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  },
  logo: { height: 70, maxWidth: 220, objectFit: "contain", marginBottom: 18 },

  // Patient bar
  patientBar: {
    display: "flex", alignItems: "center", gap: 14,
    background: "#fff", borderRadius: 16,
    padding: "12px 20px", width: "100%", maxWidth: 560,
    boxShadow: "0 2px 12px rgba(60,80,140,0.09)",
    border: "1px solid #e8edf8", marginBottom: 28,
  },
  patientAvatar: {
    width: 44, height: 44, borderRadius: "50%",
    background: "#eef3ff", display: "flex",
    alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  patientMeta: { display: "flex", flexDirection: "column" },
  patientName: { fontSize: 17, fontWeight: 700, color: "#1a2340" },
  patientDetail: { fontSize: 13, color: "#7b859a", marginTop: 2 },

  // Content
  content: { width: "100%", maxWidth: 560, flex: 1 },

  // States
  stateBox: { display: "flex", flexDirection: "column", alignItems: "center", padding: "40px 0" },
  stateText: { color: "#7b859a", fontSize: 15, marginTop: 12 },
  sectionTitle: { fontSize: 16, fontWeight: 600, color: "#1a2340", marginBottom: 14 },

  // Appointment cards
  apptList: { display: "flex", flexDirection: "column", gap: 12, marginBottom: 28 },
  apptCard: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    background: "#fff", borderRadius: 16, padding: "16px 20px",
    boxShadow: "0 2px 12px rgba(60,80,140,0.08)",
    border: "1px solid #e8edf8",
  },
  apptLeft: { display: "flex", flexDirection: "column", gap: 4 },
  apptTime: { fontSize: 20, fontWeight: 700, color: "#1a2340" },
  apptDoctor: { fontSize: 15, fontWeight: 600, color: "#374151" },
  apptSpecialty: { fontSize: 13, color: "#7b859a" },
  apptBadge: {
    display: "inline-block", padding: "2px 10px", borderRadius: 8,
    fontSize: 12, fontWeight: 600, marginTop: 4, alignSelf: "flex-start",
  },
  checkinBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "12px 22px", borderRadius: 14, border: "none",
    background: "linear-gradient(135deg, #4f8ef7, #2563eb)",
    color: "#fff", fontSize: 15, fontWeight: 600,
    cursor: "pointer", boxShadow: "0 3px 12px rgba(79,142,247,0.3)",
    whiteSpace: "nowrap", userSelect: "none", WebkitTapHighlightColor: "transparent",
    minWidth: 120,
  },
  checkinBtnLoading: { opacity: 0.75 },

  // No appointment
  noApptBox: {
    display: "flex", flexDirection: "column", alignItems: "center",
    background: "#f9fafc", borderRadius: 16, padding: "28px 20px",
    border: "1px dashed #d0d8e8", marginBottom: 28,
  },
  noApptTitle: { fontSize: 16, fontWeight: 600, color: "#374151", margin: "0 0 4px" },
  noApptSub: { fontSize: 14, color: "#7b859a", margin: 0 },

  // Doctor grid
  doctorGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
    gap: 12, marginBottom: 28,
  },
  doctorCard: {
    display: "flex", flexDirection: "column", alignItems: "center",
    gap: 8, padding: "16px 10px", borderRadius: 16,
    border: "2px solid #e8edf8", background: "#fff",
    cursor: "pointer", position: "relative",
    transition: "border-color 0.15s, box-shadow 0.15s",
    userSelect: "none", WebkitTapHighlightColor: "transparent",
  },
  doctorCardSelected: {
    borderColor: "#4f8ef7",
    boxShadow: "0 0 0 3px rgba(79,142,247,0.15)",
    background: "#f0f5ff",
  },
  doctorAvatar: {
    width: 56, height: 56, borderRadius: "50%",
    background: "#eef3ff", display: "flex",
    alignItems: "center", justifyContent: "center", overflow: "hidden",
  },
  doctorName: { fontSize: 13, fontWeight: 600, color: "#1a2340", textAlign: "center" },
  doctorSpec: { fontSize: 11, color: "#7b859a", textAlign: "center" },
  selectedTick: {
    position: "absolute", top: 8, right: 8,
    width: 22, height: 22, borderRadius: "50%",
    background: "#4f8ef7", display: "flex",
    alignItems: "center", justifyContent: "center",
  },

  // Book button
  bookBtn: {
    width: "100%", height: 60, borderRadius: 16, border: "none",
    background: "#c8d8f8", color: "#fff", fontSize: 18, fontWeight: 600,
    cursor: "not-allowed", display: "flex", alignItems: "center", justifyContent: "center",
    userSelect: "none", WebkitTapHighlightColor: "transparent",
    transition: "background 0.2s",
  },
  bookBtnActive: {
    background: "linear-gradient(135deg, #4f8ef7 0%, #2563eb 100%)",
    boxShadow: "0 4px 20px rgba(79,142,247,0.35)", cursor: "pointer",
  },

  // Back
  backBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    marginTop: 20, padding: "10px 24px", borderRadius: 12,
    border: "2px solid #d0d8e8", background: "#fff",
    color: "#4f8ef7", fontSize: 15, fontWeight: 600,
    cursor: "pointer", userSelect: "none", WebkitTapHighlightColor: "transparent",
  },

  // Thank you
  thankCard: {
    background: "#fff", borderRadius: 28, padding: "44px 40px",
    display: "flex", flexDirection: "column", alignItems: "center",
    boxShadow: "0 8px 40px rgba(60,80,140,0.12)",
    maxWidth: 440, width: "100%",
  },
  checkCircle: {
    width: 90, height: 90, borderRadius: "50%",
    background: "linear-gradient(135deg, #4f8ef7, #2563eb)",
    display: "flex", alignItems: "center", justifyContent: "center",
    marginBottom: 24, boxShadow: "0 6px 24px rgba(79,142,247,0.4)",
  },
  thankTitle: {
    fontSize: 28, fontWeight: 700, color: "#1a2340",
    margin: "0 0 10px", textAlign: "center",
  },
  thankSub: {
    fontSize: 16, color: "#6b7a99", margin: "0 0 32px",
    textAlign: "center", lineHeight: 1.6,
  },
  homeBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "14px 36px", borderRadius: 14, border: "none",
    background: "linear-gradient(135deg, #4f8ef7, #2563eb)",
    color: "#fff", fontSize: 17, fontWeight: 600, cursor: "pointer",
    boxShadow: "0 4px 16px rgba(79,142,247,0.35)",
    userSelect: "none",
  },
};
